import { Plus, Phone, Trash2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const AddCustomerDialog = ({ open, onOpenChange, newCustomer, setNewCustomer, templates, onSave, saving }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
          <DialogDescription>Add a new customer to track their onboarding</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Customer Name *</label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                placeholder="Company or Customer Name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                placeholder="email@company.com"
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Company</label>
              <Input
                value={newCustomer.company}
                onChange={(e) => setNewCustomer({...newCustomer, company: e.target.value})}
                placeholder="Company name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Person</label>
              <Input
                value={newCustomer.contact_person}
                onChange={(e) => setNewCustomer({...newCustomer, contact_person: e.target.value})}
                placeholder="Main contact name"
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                placeholder="+1 234 567 890"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={newCustomer.start_date}
                onChange={(e) => setNewCustomer({...newCustomer, start_date: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Onboarding Template</label>
            <Select 
              value={newCustomer.template_id || "none"} 
              onValueChange={(v) => setNewCustomer({...newCustomer, template_id: v === "none" ? undefined : v})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} {t.is_default && "(Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Templates pre-fill onboarding steps. You can still add/remove steps later.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#425B76] hover:bg-[#37495f]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add Customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
);

export const TemplateDialog = ({ open, onOpenChange, templates, templateForm, setTemplateForm, onSave, saving }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Onboarding Templates</DialogTitle>
          <DialogDescription>
            Create templates with default steps for new customers
          </DialogDescription>
        </DialogHeader>
        
        {templates.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Existing Templates</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {templates.map(t => (
                <div key={t.template_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div>
                    <span className="font-medium">{t.name}</span>
                    {t.is_default && <Badge className="ml-2 text-xs">Default</Badge>}
                    <span className="text-sm text-slate-500 ml-2">
                      ({t.steps?.length || 0} steps)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-3">Create New Template</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Template Name *</label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  placeholder="e.g., Standard Onboarding"
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={templateForm.is_default}
                    onCheckedChange={(c) => setTemplateForm({...templateForm, is_default: c})}
                  />
                  <span className="text-sm">Set as default template</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Steps</label>
              <div className="space-y-2 mt-2">
                {templateForm.steps.map((step, i) => (
                  <div key={`step-${i}-${step.title?.slice(0,10) || 'new'}`} className="flex items-center gap-2">
                    <Input
                      value={step.title}
                      onChange={(e) => {
                        const newSteps = [...templateForm.steps];
                        newSteps[i].title = e.target.value;
                        setTemplateForm({...templateForm, steps: newSteps});
                      }}
                      placeholder={`Step ${i + 1} title`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newSteps = templateForm.steps.filter((_, idx) => idx !== i);
                        setTemplateForm({...templateForm, steps: newSteps.length ? newSteps : [{ title: "", description: "" }]});
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTemplateForm({
                    ...templateForm,
                    steps: [...templateForm.steps, { title: "", description: "" }]
                  })}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#425B76] hover:bg-[#37495f]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
);
